'use client';
import React from 'react';
import { FieldError } from 'react-hook-form';
import { IRepository } from '@/types';
import { ROOT_LAYOUT_DOM_NODE_ID } from '@/config/appSettings';
import { useModal } from 'react-hooks-use-modal';
import { IRepositoryBranch, createBranch } from '@/lib/repositoryBranchesApi';
import BranchSelector from '@/components/Repository/BranchSelector';
import Button from '@/ui/Button';
import * as Styled from './styled';

import * as Icon from '@/ui/Icon';
import Input from '@/components/Input';

const branchNameTests = [
  { regex: /^.{2,}$/, message: 'at least 2 characters' },
  { regex: /^[a-zA-Z0-9/_-]+$/, message: 'only alphanumeric, dashes slashes and underscores' },
  { regex: /^[^_/-].*/, message: 'cannot start with an underscore, slash, or dash' },
];

// regex to allow for alphanumeric, dashes, slashes, and underscores
// const branchNameRegex = /^[a-zA-Z0-9-_]+$/;

const getBranchNameErrors = (branchName: string, branches: IRepositoryBranch[]): FieldError[] => {
  const branchExists = (branchName?: string) => {
    return !!branches.some((branch) => branch.name === branchName);
  };

  const errors = branchNameTests.map((test) => !branchName?.match(test.regex) && test.message).filter(Boolean);

  if (branchExists(branchName)) {
    errors.push('Branch already exists');
  }

  return errors.map((error) => ({ type: 'error', message: error }) as FieldError);
};

const BranchCreate: React.FC<{ repository: IRepository; branches: IRepositoryBranch[]; disabled?: boolean }> = ({
  repository,
  branches,
  disabled = false,
}) => {
  const [Modal, open, close, isOpen] = useModal(ROOT_LAYOUT_DOM_NODE_ID, {
    preventScroll: true,
  });

  const [sourceBranch, setSourceBranch] = React.useState<string>(repository?.default_branch || 'main');
  const [branchName, setBranchName] = React.useState<string>('');

  const branchNameErrors = getBranchNameErrors(branchName, branches);
  const isValid = branchNameErrors.length === 0;

  const create = async () => {
    if (!isValid) return;
    const { branch, error } = await createBranch({
      namespace: repository.namespace,
      repositoryName: repository.name,
      from_name: sourceBranch,
      new_name: branchName,
    });

    if (branch && !error) {
      window.location.reload();
      close();
    }
  };

  return (
    <>
      <Button.Black small onClick={open} disabled={disabled}>
        New branch
      </Button.Black>
      <Modal>
        <Styled.BranchCreateModal>
          <Styled.ModalHeader>
            <span>Create a branch</span>
            <Button.Text onClick={close}>
              <Icon.X size="14" />
            </Button.Text>
          </Styled.ModalHeader>
          <Styled.ModalBody>
            {/* <p>Here's some content to go with</p> */}

            <Input
              label="Branch name"
              name="branch-name"
              value={branchName}
              autoFocus
              error={branchNameErrors[0]}
              onChange={(e) => setBranchName(e.target.value)}
            />
            <BranchSelector
              label="Source"
              name="test"
              branches={branches}
              selected={sourceBranch}
              onChange={setSourceBranch}
            />
          </Styled.ModalBody>
          <Styled.ModalFooter>
            <Button.Secondary small onClick={close}>
              Cancel
            </Button.Secondary>
            <Button.Black disabled={!isValid} small onClick={create}>
              Create branch
            </Button.Black>
          </Styled.ModalFooter>
        </Styled.BranchCreateModal>
      </Modal>
    </>
  );
};

export default BranchCreate;
